import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { DEFAULT_TRANSLATIONS } from './translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
});

const STORAGE_KEY = 'app_language';

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'en'; }
    catch { return 'en'; }
  });
  const [hasUserChosen, setHasUserChosen] = useState(() => {
    try { return !!localStorage.getItem(STORAGE_KEY); }
    catch { return false; }
  });
  const [overrides, setOverrides] = useState({ en: {}, no: {} });

  useEffect(() => {
    const fetchTranslations = () => {
      // Cache-bust to defeat any intermediate CDN/SW cache. Admin edits MUST go live.
      axios.get(`${API}/translations`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      })
        .then(r => setOverrides(r.data || { en: {}, no: {} }))
        .catch((e) => { console.warn('translations fetch failed', e?.message); });
    };
    fetchTranslations();
    // Re-fetch whenever an inline edit succeeds (see /app/frontend/src/components/EditMode.js).
    window.addEventListener('translations-updated', fetchTranslations);
    return () => window.removeEventListener('translations-updated', fetchTranslations);
  }, []);

  // Auto-detect language based on visitor IP if they haven't chosen one yet
  useEffect(() => {
    if (hasUserChosen) return;
    axios.get(`${API}/geo/lang`)
      .then(r => {
        const suggested = r.data?.suggested_lang;
        if (suggested && ['en', 'no'].includes(suggested)) {
          setLangState(suggested);
          try { document.documentElement.lang = suggested; } catch (e) { /* noop */ }
        }
      })
      .catch(() => { /* fall back to default */ });
  }, [hasUserChosen]);

  const setLang = useCallback((next) => {
    setLangState(next);
    setHasUserChosen(true);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { console.warn('localStorage unavailable', e?.message); }
    try { document.documentElement.lang = next; } catch (e) { console.warn('document.lang set failed', e?.message); }
  }, []);

  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (e) { console.warn('document.lang set failed', e?.message); }
  }, [lang]);

  const t = useCallback((key, vars) => {
    const fromOverride = overrides?.[lang]?.[key];
    const fromDefault = DEFAULT_TRANSLATIONS?.[lang]?.[key];
    const fromEnDefault = DEFAULT_TRANSLATIONS?.en?.[key];
    let value = fromOverride || fromDefault || fromEnDefault || key;
    if (vars && typeof value === 'string') {
      Object.entries(vars).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return value;
  }, [lang, overrides]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
